﻿package com.mgacreative.hotelru.domain.repository

import com.mgacreative.hotelru.core.network.SupabaseNetworkClient
import com.mgacreative.hotelru.core.tenant.TenantSession
import com.mgacreative.hotelru.domain.model.PosCategory
import com.mgacreative.hotelru.domain.model.PosItem
import io.github.jan.supabase.postgrest.postgrest
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.datetime.Clock
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime

interface PosRepository {
    fun observeCategories(): Flow<List<PosCategory>>
    fun observeItems(): Flow<List<PosItem>>
    fun observeOpenOrders(): Flow<List<com.mgacreative.hotelru.domain.model.PosOrder>>
    suspend fun loadMenu()
    suspend fun saveCategory(categoryName: String): PosCategory?
    suspend fun saveMenuItem(name: String, categoryId: String, price: Double, isAvailable: Boolean = true): PosItem?
    suspend fun deleteMenuItem(itemId: String): Boolean
    suspend fun loadOpenOrders()
    suspend fun getOrderItems(orderId: String): List<com.mgacreative.hotelru.domain.model.PosOrderItem>
    suspend fun saveOrder(orderId: String?, tableName: String, items: Map<PosItem, Int>, totalAmount: Double): String?
    suspend fun takePayment(orderId: String?, tableName: String, items: Map<PosItem, Int>, totalAmount: Double, reservationId: String? = null): Boolean
}

class PosRepositoryImpl(
    private val networkClient: SupabaseNetworkClient,
    private val tenantSession: TenantSession,
    private val financeRepository: FinanceRepository
) : PosRepository {

    private val _categories = MutableStateFlow<List<PosCategory>>(emptyList())
    private val _items = MutableStateFlow<List<PosItem>>(emptyList())
    private val _openOrders = MutableStateFlow<List<com.mgacreative.hotelru.domain.model.PosOrder>>(emptyList())

    override fun observeCategories(): Flow<List<PosCategory>> = _categories.asStateFlow()
    override fun observeItems(): Flow<List<PosItem>> = _items.asStateFlow()
    override fun observeOpenOrders(): Flow<List<com.mgacreative.hotelru.domain.model.PosOrder>> = _openOrders.asStateFlow()

    override suspend fun loadMenu() {
        try {
            val tenantId = tenantSession.currentTenantId ?: return

            val categoriesResponse = networkClient.client.postgrest["pos_categories"]
                .select { filter { eq("tenant_id", tenantId) } }
                .decodeList<PosCategory>()

            val itemsResponse = networkClient.client.postgrest["pos_items"]
                .select { filter { eq("tenant_id", tenantId) } }
                .decodeList<PosItem>()

            _categories.value = categoriesResponse
            _items.value = itemsResponse
        } catch (e: Exception) {
            e.printStackTrace()
            networkClient.handleError(e)
        }
    }

    override suspend fun saveCategory(categoryName: String): PosCategory? {
        return try {
            val tenantId = tenantSession.currentTenantId ?: return null
            @kotlinx.serialization.Serializable
            data class CategoryRequest(val tenant_id: String, val name: String)
            
            val inserted = networkClient.client.postgrest["pos_categories"]
                .insert(CategoryRequest(tenantId, categoryName)) { select() }
                .decodeSingle<PosCategory>()
            loadMenu()
            inserted
        } catch (e: Exception) {
            e.printStackTrace()
            networkClient.handleError(e)
            null
        }
    }

    override suspend fun saveMenuItem(name: String, categoryId: String, price: Double, isAvailable: Boolean): PosItem? {
        return try {
            val tenantId = tenantSession.currentTenantId ?: return null
            @kotlinx.serialization.Serializable
            data class ItemRequest(
                val tenant_id: String,
                val category_id: String,
                val name: String,
                val price: Double,
                val is_available: Boolean
            )
            
            val inserted = networkClient.client.postgrest["pos_items"]
                .insert(ItemRequest(tenantId, categoryId, name, price, isAvailable)) { select() }
                .decodeSingle<PosItem>()
            loadMenu()
            inserted
        } catch (e: Exception) {
            e.printStackTrace()
            networkClient.handleError(e)
            null
        }
    }

    override suspend fun deleteMenuItem(itemId: String): Boolean {
        return try {
            networkClient.client.postgrest["pos_items"].delete {
                filter { eq("id", itemId) }
            }
            loadMenu()
            true
        } catch (e: Exception) {
            e.printStackTrace()
            networkClient.handleError(e)
            false
        }
    }

    override suspend fun loadOpenOrders() {
        try {
            val tenantId = tenantSession.currentTenantId ?: return
            val orders = networkClient.client.postgrest["pos_orders"]
                .select { 
                    filter { 
                        eq("tenant_id", tenantId)
                        eq("status", "OPEN")
                    } 
                }
                .decodeList<com.mgacreative.hotelru.domain.model.PosOrder>()
            _openOrders.value = orders
        } catch (e: Exception) {
            e.printStackTrace()
            networkClient.handleError(e)
        }
    }

    override suspend fun getOrderItems(orderId: String): List<com.mgacreative.hotelru.domain.model.PosOrderItem> {
        return try {
            val tenantId = tenantSession.currentTenantId ?: return emptyList()
            networkClient.client.postgrest["pos_order_items"]
                .select { 
                    filter { 
                        eq("tenant_id", tenantId)
                        eq("order_id", orderId)
                    } 
                }
                .decodeList<com.mgacreative.hotelru.domain.model.PosOrderItem>()
        } catch (e: Exception) {
            e.printStackTrace()
            networkClient.handleError(e)
            emptyList()
        }
    }

    override suspend fun saveOrder(orderId: String?, tableName: String, items: Map<PosItem, Int>, totalAmount: Double): String? {
        return try {
            val tenantId = tenantSession.currentTenantId ?: return null
            val finalOrderId = insertOrUpdateOrder(orderId, tenantId, tableName, "OPEN", totalAmount, null) ?: return null
            insertOrderItems(tenantId, finalOrderId, items)
            loadOpenOrders() // refresh open orders
            finalOrderId
        } catch (e: Exception) {
            e.printStackTrace()
            networkClient.handleError(e)
            null
        }
    }

    override suspend fun takePayment(orderId: String?, tableName: String, items: Map<PosItem, Int>, totalAmount: Double, reservationId: String?): Boolean {
        return try {
            val tenantId = tenantSession.currentTenantId ?: return false
            val status = if (reservationId != null) "ROOM_CHARGE" else "PAID"
            val finalOrderId = insertOrUpdateOrder(orderId, tenantId, tableName, status, totalAmount, reservationId) ?: return false
            insertOrderItems(tenantId, finalOrderId, items)

            if (reservationId == null) {
                val currentDate = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).date.toString()
                financeRepository.addCashMovement(
                    type = "INCOME",
                    amount = totalAmount,
                    description = "Restoran Geliri - $tableName",
                    date = currentDate
                )
            } else {
                @kotlinx.serialization.Serializable
                data class FolioItemRequest(
                    val tenant_id: String,
                    val reservation_id: String,
                    val category: String,
                    val description: String,
                    val amount: Double
                )
                val description = items.entries.joinToString(", ") { "${it.key.name} x${it.value}" }
                networkClient.client.postgrest["folio_items"].insert(
                    FolioItemRequest(
                        tenant_id = tenantId,
                        reservation_id = reservationId,
                        category = "RESTAURANT",
                        description = "Restoran Harcaması ($tableName): $description",
                        amount = totalAmount
                    )
                )
            }
            loadOpenOrders() // refresh open orders
            true
        } catch (e: Exception) {
            e.printStackTrace()
            networkClient.handleError(e)
            false
        }
    }

    private suspend fun insertOrUpdateOrder(
        orderId: String?, 
        tenantId: String, 
        tableName: String, 
        status: String, 
        totalAmount: Double, 
        reservationId: String?
    ): String? {
        @kotlinx.serialization.Serializable
        data class OrderRequest(
            val tenant_id: String,
            val table_name: String,
            val status: String,
            val total_amount: Double,
            val reservation_id: String?
        )
        val orderRequest = OrderRequest(tenantId, tableName, status, totalAmount, reservationId)
        
        @kotlinx.serialization.Serializable
        data class InsertedOrder(val id: String)

        if (orderId != null) {
            networkClient.client.postgrest["pos_orders"].update(orderRequest) {
                filter { eq("id", orderId) }
            }
            return orderId
        } else {
            val insertedOrders = networkClient.client.postgrest["pos_orders"]
                .insert(orderRequest) { select() }
                .decodeList<InsertedOrder>()
            return insertedOrders.firstOrNull()?.id
        }
    }

    private suspend fun insertOrderItems(tenantId: String, orderId: String, items: Map<PosItem, Int>) {
        // Delete existing items first
        networkClient.client.postgrest["pos_order_items"].delete {
            filter { eq("order_id", orderId) }
        }

        if (items.isEmpty()) return

        @kotlinx.serialization.Serializable
        data class OrderItemRequest(
            val tenant_id: String,
            val order_id: String,
            val item_id: String?,
            val manual_name: String?,
            val quantity: Int,
            val price_at_time: Double,
            val total_price: Double
        )

        val orderItemsRequest = items.map { (item, qty) ->
            OrderItemRequest(
                tenant_id = tenantId,
                order_id = orderId,
                item_id = if (item.id.isEmpty()) null else item.id,
                manual_name = if (item.id.isEmpty()) item.name else null,
                quantity = qty,
                price_at_time = item.price,
                total_price = item.price * qty
            )
        }
        networkClient.client.postgrest["pos_order_items"].insert(orderItemsRequest)
    }
}

